// lib/bridgeCalldata.js
//
// Client-side calldata builders for the LIVE mainnet bridge. The customer's
// own wallet signs both transactions — this module only encodes the bytes;
// it never talks to a server and never holds a key. Uses viem's
// encodeFunctionData (proper ABI encoding — the selector viem computes MUST
// match cpp/src/bridge/bridge_calldata.hpp's pinned constants; both are unit
// tested against the same signatures so a drift would be caught).

import { encodeFunctionData, decodeEventLog } from 'viem';
import { ERC20_APPROVE_ABI, BRIDGE_SENDER_ABI, BRIDGE_RECEIVER_ABI } from './bridgeAbi';

/**
 * A fresh, cryptographically-random bytes32 idempotency key for
 * requestSwap's `idempotencyKey` param. Not reused across submissions — a
 * retry from the UI should generate a new one (the contract's `swapId`
 * derivation also folds in block.number + sender, so even a resubmit with
 * the same key at a different block produces a different, valid swapId;
 * this is just defence-in-depth + a clean idempotency story per attempt).
 */
export function newIdempotencyKeyHex() {
  const bytes = new Uint8Array(32);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 32; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return `0x${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/** approve(spender, amount) calldata for the source kStable. */
export function encodeApproveCalldata(spenderAddress, amountWei) {
  return encodeFunctionData({
    abi: ERC20_APPROVE_ABI,
    functionName: 'approve',
    args: [spenderAddress, BigInt(amountWei)],
  });
}

/**
 * requestSwap(kStable, amount, destChainId, recipient, minOutAmount,
 * idempotencyKey) calldata for the source BridgeSenderUpgradeable.
 */
export function encodeRequestSwapCalldata({
  kStableAddress,
  amountWei,
  destChainId,
  recipient,
  minOutWei,
  idempotencyKeyHex,
}) {
  return encodeFunctionData({
    abi: BRIDGE_SENDER_ABI,
    functionName: 'requestSwap',
    args: [
      kStableAddress,
      BigInt(amountWei),
      BigInt(destChainId),
      recipient,
      BigInt(minOutWei),
      idempotencyKeyHex,
    ],
  });
}

/**
 * Decode the ChainSwapRequested event out of a burn transaction's receipt
 * logs. This is the ONLY reliable way to learn `swapId` client-side — the
 * contract derives it from (chainId, block.number, sender, idempotencyKey)
 * at execution time, which the client cannot predict in advance.
 *
 * Returns null if no matching log is found (e.g. wrong receipt, or the log
 * comes from a different contract than `bridgeSenderAddress`).
 */
export function decodeChainSwapRequested(receipt, bridgeSenderAddress) {
  if (!receipt?.logs) return null;
  const target = String(bridgeSenderAddress).toLowerCase();
  for (const log of receipt.logs) {
    if (String(log.address).toLowerCase() !== target) continue;
    try {
      const decoded = decodeEventLog({
        abi: BRIDGE_SENDER_ABI,
        data: log.data,
        topics: log.topics,
      });
      if (decoded.eventName === 'ChainSwapRequested') {
        return decoded.args;
      }
    } catch (_) {
      // Not this event (or a log we can't decode) — keep scanning.
    }
  }
  return null;
}

/**
 * Decode the ChainSwapFulfilled event out of a fulfill transaction's receipt
 * logs (or a getLogs result) — used once useBridgeDelivery observes
 * fulfilled(swapId) flip true and wants the destination tx hash to link.
 */
export function decodeChainSwapFulfilled(log) {
  try {
    const decoded = decodeEventLog({
      abi: BRIDGE_RECEIVER_ABI,
      data: log.data,
      topics: log.topics,
    });
    if (decoded.eventName === 'ChainSwapFulfilled') return decoded.args;
  } catch (_) {
    // not this event
  }
  return null;
}

export { BRIDGE_SENDER_ABI, BRIDGE_RECEIVER_ABI };
